import { useState } from 'react';
import {
  useStreamPublisherStore,
  usePlatformStreamStore,
} from '@features/streaming';
import { useMediaCapture } from '@features/media-capture';
import { useVideoFilterStore } from '@features/video-filters';
import { useRoomStore } from '@features/room';
import { ActionButton, PlayIcon, StopIcon, RecordIcon, VideoCameraIcon } from '@shared/ui';
import { getConnectionStatusLabel } from '@entities/stream';

/**
 * Preview-page streaming controls. The single entry point is
 * "Start Streaming" — it does everything the user needs in one click:
 *
 *   1. Make sure the local camera/mic stream is active.
 *   2. Apply the current video filter.
 *   3. Publish tracks on the room (auto-joining a room if the user
 *      hasn't already done so on the Streaming tab).
 *   4. Kick off the platform egress via the token server.
 *
 * "Stop Streaming" runs the steps in reverse: stop the platform egress,
 * unpublish tracks, and only leave the room if we created it for the
 * stream ourselves (so we don't drop co-hosts the user invited).
 */
export function StreamControls() {
  const lifecycle = useStreamPublisherStore((state) => state.lifecycle);
  const recording = useStreamPublisherStore((state) => state.recording);
  const startStream = useStreamPublisherStore((state) => state.startStream);
  const stopStream = useStreamPublisherStore((state) => state.stopStream);
  const toggleRecording = useStreamPublisherStore((state) => state.toggleRecording);

  const { start, settings, getOutputStream, state: captureState } = useMediaCapture();
  const processStream = useVideoFilterStore((state) => state.processStream);
  const stopProcessing = useVideoFilterStore((state) => state.stopProcessing);

  const tokenServerUrl = useRoomStore((state) => state.settings.tokenServerUrl);
  const leave = useRoomStore((state) => state.leave);
  const joinedForStreamingOnly = useRoomStore((state) => state.joinedForStreamingOnly);
  const hasRoom = useRoomStore((state) => state.room !== null);

  const platformState = usePlatformStreamStore((state) => state.state);
  const platformSession = usePlatformStreamStore((state) => state.session);
  const startPlatformStream = usePlatformStreamStore((state) => state.start);
  const stopPlatformStream = usePlatformStreamStore((state) => state.stop);

  const [startError, setStartError] = useState<string | null>(null);

  const isStarting = lifecycle === 'starting' || platformState === 'starting';
  const isStopping = lifecycle === 'stopping' || platformState === 'stopping';
  const isLive = lifecycle === 'live' || platformState === 'live';
  const canStart = !isLive && !isStarting;
  const canStop = isLive || isStopping;

  const handleStart = async () => {
    setStartError(null);

    try {
      await start(settings);

      const localStream = getOutputStream();
      if (!localStream) {
        throw new Error('Media capture started without an output stream.');
      }

      const filtered = await processStream(localStream);

      // 1. publish tracks on the room (auto-joins if no room is active)
      await startStream(filtered);

      // 2. start platform egress — needs the (possibly just-created) room name
      const currentRoomId = useRoomStore.getState().room?.id;
      if (!currentRoomId) {
        throw new Error('Streaming started but no room is connected.');
      }

      await startPlatformStream(tokenServerUrl, currentRoomId);
    } catch (error) {
      setStartError(error instanceof Error ? error.message : 'Failed to start streaming');
      // best-effort cleanup
      try {
        if (usePlatformStreamStore.getState().session) {
          await stopPlatformStream(tokenServerUrl);
        }
      } catch {
        /* ignore */
      }
      try {
        await stopStream();
      } catch {
        /* ignore */
      }
      stopProcessing();

      if (joinedForStreamingOnly) {
        try {
          await leave();
        } catch {
          /* ignore */
        }
      }
    }
  };

  const handleStop = async () => {
    setStartError(null);

    try {
      // 1. stop platform egress first so the platform goes dark cleanly
      if (platformSession) {
        try {
          await stopPlatformStream(tokenServerUrl);
        } catch {
          /* keep going — publisher teardown matters more */
        }
      }

      // 2. unpublish tracks
      try {
        await stopStream();
      } catch {
        /* ignore */
      }
      stopProcessing();

      // 3. only leave the room if the streaming feature created it
      if (joinedForStreamingOnly) {
        await leave();
      }
    } catch (error) {
      setStartError(error instanceof Error ? error.message : 'Failed to stop streaming');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <ActionButton
          icon={<PlayIcon className="h-4 w-4" />}
          variant="success"
          disabled={!canStart}
          onClick={() => void handleStart()}
        >
          {isStarting ? 'Starting…' : 'Start Streaming'}
        </ActionButton>

        <ActionButton
          icon={<StopIcon className="h-4 w-4" />}
          variant="danger"
          disabled={!canStop}
          onClick={() => void handleStop()}
        >
          {isStopping ? 'Stopping…' : 'Stop Streaming'}
        </ActionButton>

        <ActionButton
          icon={<RecordIcon className="h-4 w-4" />}
          variant={recording === 'recording' ? 'danger' : 'neutral'}
          disabled={!canStop}
          onClick={toggleRecording}
        >
          {recording === 'recording' ? 'Recording…' : recording === 'saving' ? 'Saving…' : 'Record'}
        </ActionButton>

        <ActionButton
          icon={<VideoCameraIcon className="h-4 w-4" />}
          variant="neutral"
          disabled
          title="Virtual camera requires OS-level integration — not implemented yet"
        >
          Virtual Camera (soon)
        </ActionButton>
      </div>

      {startError && (
        <p className="rounded-lg border border-status-live/30 bg-status-live/10 px-3 py-2 text-xs text-status-live">
          {startError}
        </p>
      )}

      <p className="text-xs text-text-muted">
        Camera: {getConnectionStatusLabel(captureState === 'active')} · Room:{' '}
        {hasRoom ? (joinedForStreamingOnly ? 'auto (stream-only)' : 'manual') : 'none yet'}
      </p>
    </div>
  );
}
